/* tempexp_18086-start */
import { logger } from 'lib/logger';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_MOBILE_OR_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { REFERRAL_ANALYTIC_NAME } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { BillingDataReferralSelectors } from 'reactApp/modules/billingData/billingData.referral.selectors';
import { ReferralProgramActions } from 'reactApp/modules/referralProgram/referralProgram.module';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { captureException } from 'reactApp/utils/tracer';

const REFERRAL_LINK_COPY = 'REFERRAL_LINK_COPY';
const REFERRAL_LINK_ERROR = 'REFERRAL_LINK_ERROR';

export function useReferralLink(email: string | null = null) {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(ReferralProgramActions.getReferralLinkRequest());
    }, [dispatch]);
    const referralLink = useSelector(ReferralProgramSelectors.getLink);
    const referralsCount = useSelector(BillingDataReferralSelectors.getReferralsCount);
    const referralQuota = useSelector(BillingDataReferralSelectors.getReferralCommonQuota);
    const isNativeShareSupported = IS_MOBILE_OR_TABLET_BROWSER && 'share' in navigator && typeof navigator.share === 'function';
    const buttonText = isNativeShareSupported ? 'Поделиться' : 'Скопировать';
    const copyOrShare = useCallback(async () => {
        if (!referralLink) {
            return;
        }

        emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_MODAL_COPY);
        sendPaymentGa({
            action: 'click',
            eventCategory: ECategoryGa.referalModal,
            name: email ? 'referal_friends_info' : REFERRAL_ANALYTIC_NAME,
            button_name: 'copy_referal_link',
            referal_friend_id: email,
            friends_bonus_gb: referralQuota.space,
            referal_friends_invited: referralsCount,
        });

        if (isNativeShareSupported) {
            try {
                await navigator.share({
                    url: referralLink,
                });
            } catch (error) {
                sendXray(['referralLink', 'share', 'error']);
                logger.error(error);
                captureException(error, { issueKey: `referralLink/share/error` });
            }
        } else {
            try {
                await navigator.clipboard.writeText(referralLink);
                dispatch(
                    showSnackbarAction({
                        text: 'Ссылка скопирована — отправьте её другу',
                        type: SnackbarTypes.success,
                        id: REFERRAL_LINK_COPY,
                        closable: true,
                    })
                );
            } catch (error) {
                sendXray(['referralLink', 'copy', 'error']);
                logger.error(error);
                captureException(error, { issueKey: `referralLink/copy/error` });
                dispatch(
                    showSnackbarAction({
                        text: 'Не удалось скопировать ссылку',
                        type: SnackbarTypes.failure,
                        id: REFERRAL_LINK_ERROR,
                        closable: true,
                    })
                );
            }
        }
    }, [referralLink, dispatch]);

    return { copyOrShare, buttonText, disabled: !referralLink };
}

/* tempexp_18086-end */
